// src/pages/admin/components/SuperAdminTaskManager.jsx
import { useEffect, useState } from "react";
import axios from "axios";
import { Tabs, Tab, Form, Row, Col, Button } from "react-bootstrap";

const SuperAdminTaskManager = ({ userInfo }) => {
    const [tasks, setTasks] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [newTask, setNewTask] = useState({ description: "", assignee: "", dueDate: "", frequency: "", note: "" });
    const [filteredTasks, setFilteredTasks] = useState({});

    useEffect(() => {
        fetchTasks();
        fetchAdmins();
    }, []);

    const fetchTasks = async () => {
        const { data } = await axios.get("/api/tasks/admin");
        setTasks(data);
        setFilteredTasks(categoriseTasks(data));
    };

    const fetchAdmins = async () => {
        const { data } = await axios.get("/api/users/admins?isAdmin=true");
        setAdmins(data);
    };

    const categoriseTasks = (taskList) => ({
        pending: taskList.filter((t) => t.status === "pending"),
        processing: taskList.filter((t) =>
            t.status === "processing" && (t.frequency === "one-time" || !t.frequency)
        ),
        completed: taskList.filter((t) => t.status === "completed"),
        declined: taskList.filter((t) => t.status === "declined"),
        ongoing: taskList.filter((t) =>
            (t.frequency === "weekly" || t.frequency === "fortnightly") && t.status === "processing"
        ),
        stopped: taskList.filter((t) => t.status === "stopped"),
    });

    const assignTask = async () => {
        const addDays = (date: Date, days: number): Date => {
            const copy = new Date(date);
            copy.setDate(copy.getDate() + days);
            return copy;
        };

        const taskToSubmit = {
            ...newTask,
            dueDate:
                newTask.frequency === "weekly"
                    ? addDays(new Date(), 7)
                    : newTask.frequency === "fortnightly"
                        ? addDays(new Date(), 14)
                        : newTask.dueDate,
            frequency: newTask.frequency || "one-time",
            assignee: newTask.assignee,
        };
        await axios.post("/api/tasks/admin/create", taskToSubmit);
        setNewTask({ description: "", assignee: "", dueDate: "", frequency: "", note: "" });
        fetchTasks();
    };

    return (
        <>
            <h5>Assign New Task</h5>
            <Form className="p-4 mb-4 bg-light rounded shadow-sm border">
                <Row className="mb-3 align-items-end">
                    <Col md={6} className="mb-2">
                        <Form.Label><strong>Task</strong></Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Enter task description"
                            value={newTask.description}
                            onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                        />
                    </Col>
                    <Col md={3} className="mb-2">
                        <Form.Label><strong>Assigned To</strong></Form.Label>
                        <Form.Select value={newTask.assignee} onChange={(e) => setNewTask({ ...newTask, assignee: e.target.value })}>
                            <option value="">Select Admin</option>
                            {admins.map((admin) => (
                                <option key={admin._id} value={admin._id}>{admin.name}</option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col md={3} className="mb-2">
                        <strong>Frequency</strong><br />
                        <div className="d-flex">
                            <Form.Check
                                label="One-time"
                                type="radio"
                                name="frequency"
                                onChange={() => setNewTask({ ...newTask, frequency: "one-time" })}
                                checked={newTask.frequency === "one-time" || !newTask.frequency}
                                className="me-3"
                            />
                            <Form.Check
                                label="Weekly"
                                type="radio"
                                name="frequency"
                                onChange={() => setNewTask({ ...newTask, frequency: "weekly" })}
                                checked={newTask.frequency === "weekly"}
                                className="me-3"
                            />
                            <Form.Check
                                label="Fortnightly"
                                type="radio"
                                name="frequency"
                                onChange={() => setNewTask({ ...newTask, frequency: "fortnightly" })}
                                checked={newTask.frequency === "fortnightly"}
                            />
                        </div>
                    </Col>
                </Row>
                <Row className="align-items-center">
                    <Col md={6} className="mb-2">
                        <Form.Label><strong>Note</strong></Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Additional instructions"
                            value={newTask.note}
                            onChange={(e) => setNewTask({ ...newTask, note: e.target.value })}
                        />
                    </Col>
                    <Col md={3} className="mb-2">
                        <Form.Label><strong>Due Date</strong></Form.Label>
                        <Form.Control
                            type="date"
                            value={newTask.dueDate || ""}
                            onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                            disabled={newTask.frequency === "weekly" || newTask.frequency === "fortnightly"}
                        />
                    </Col>
                    <Col md={3} className="text-end">
                        <Button variant="primary" onClick={assignTask}>Assign Task</Button>
                    </Col>
                </Row>
            </Form>


            <Tabs defaultActiveKey="pending" className="mt-3">
                {["pending", "processing", "ongoing", "completed", "declined", "stopped"].map((key) => (
                    <Tab eventKey={key} title={`${key[0].toUpperCase() + key.slice(1)} Tasks`} key={key}>
                        <div className="table-responsive mt-3">
                            <table className="table table-bordered table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th>Description</th>
                                        <th>Assigned To</th>
                                        <th>Assigned Date</th>
                                        <th>Due Date</th>
                                        <th>Frequency</th>
                                        <th>Status</th>
                                        <th>Note</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTasks[key]?.length > 0 ? (
                                        filteredTasks[key].map((task) => (
                                            <tr key={task._id}>
                                                <td style={{ width: "20%" }}>{task.description}</td>
                                                <td style={{ width: "10%" }}>{task.assignee?.name || "N/A"}</td>
                                                <td style={{ width: "10%" }}>{task.createdAt.slice(0, 10)}</td>
                                                <td style={{ width: "8%" }}>{task.dueDate ? task.dueDate.slice(0, 10) : "—"}</td>
                                                <td style={{ width: "8%" }}>{task.frequency || "One-time"}</td>
                                                <td style={{ width: "8%" }}>{task.status}</td>
                                                <td style={{ width: "35%" }}>{task.note || "—"}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="text-center">No tasks found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Tab>
                ))}
            </Tabs>

        </>
    );
};

export default SuperAdminTaskManager;
